/**
 * 新对话启动提示数据
 * 模仿 ChatGPT 的二级交互设计
 */

import { getTimeBasedGreeting } from '../utils/timeGreetingUtils';

export interface PromptCategory {
  id: string;
  title: string;
  icon: string;
  description: string;
  prompts: PromptItem[];
}

export interface PromptItem {
  id: string;
  text: string;
  description?: string;
}

// 随机欢迎语（通用版本，不包含时间相关内容）
// 这些消息将通过 i18n 系统动态获取
export const getWelcomeMessages = (t: any) => [
  t('v3/chat:welcome.message1', "你好！我是你的智能助手 ✨"),
  t('v3/chat:welcome.message2', "嗨！想聊什么呢？ 🌟"),
  t('v3/chat:welcome.message3', "欢迎！有什么可以帮你的吗？ 💫"),
  t('v3/chat:welcome.message4', "你好呀！准备开始新的对话了吗？ 🚀"),
  t('v3/chat:welcome.message5', "Hi！我在这里随时为你服务 😊"),
  t('v3/chat:welcome.message6', "又见面了！有什么想法吗？ 🌈"),
  t('v3/chat:welcome.message7', "让我们一起创造些什么吧 🎨"),
  t('v3/chat:welcome.message8', "准备好迎接新的挑战了吗？ 💪"),
  t('v3/chat:welcome.message9', "想要完成什么目标呢？ 🎯"),
  t('v3/chat:welcome.message10', "我来帮你把想法变成现实 ⚡")
];

// 随机副标题
// 这些消息将通过 i18n 系统动态获取
export const getSubMessages = (t: any) => [
  t('v3/chat:subtitle.message1', "选择一个话题开始我们的对话吧"),
  t('v3/chat:subtitle.message2', "我可以帮你处理各种任务和问题"),
  t('v3/chat:subtitle.message3', "让我们一起完成一些有趣的事情"),
  t('v3/chat:subtitle.message4', "告诉我你想做什么，我来帮你"),
  t('v3/chat:subtitle.message5', "从下面选择一个方向，或者直接告诉我你的想法"),
  t('v3/chat:subtitle.message6', "我准备好了，你呢？"),
  t('v3/chat:subtitle.message7', "无论是工作还是生活，我都能为你提供帮助"),
  t('v3/chat:subtitle.message8', "每一次对话都是新的开始"),
  t('v3/chat:subtitle.message9', "让我们把想法变成现实"),
  t('v3/chat:subtitle.message10', "一起探索无限可能吧")
];

// 提示分类数据
// 这些数据将通过 i18n 系统动态获取
export const getPromptCategories = (t: any): PromptCategory[] => [
  {
    id: 'create',
    title: t('v3/chat:categories.create.title', '创建'),
    icon: '✨',
    description: t('v3/chat:categories.create.description', '创建任务、计划和内容'),
    prompts: [
      {
        id: 'create-tasks',
        text: t('v3/chat:categories.create.prompts.tasks.text', '帮我创建今天的任务，我接下来要'),
        description: t('v3/chat:categories.create.prompts.tasks.description', '规划今日待办事项')
      },
      {
        id: 'create-schedule',
        text: t('v3/chat:categories.create.prompts.schedule.text', '安排一个新的会议，关于'),
        description: t('v3/chat:categories.create.prompts.schedule.description', '创建会议安排')
      },
      {
        id: 'create-reminder',
        text: t('v3/chat:categories.create.prompts.reminder.text', '设置提醒我'),
        description: t('v3/chat:categories.create.prompts.reminder.description', '创建重要提醒')
      },
      {
        id: 'create-project',
        text: t('v3/chat:categories.create.prompts.project.text', '帮我规划一个新项目，主题是'),
        description: t('v3/chat:categories.create.prompts.project.description', '项目规划助手')
      },
      {
        id: 'create-content',
        text: t('v3/chat:categories.create.prompts.content.text', '帮我写一份工作总结'),
        description: t('v3/chat:categories.create.prompts.content.description', '内容创作助手')
      }
    ]
  },
  {
    id: 'analyze',
    title: t('v3/chat:categories.analyze.title', '分析'),
    icon: '📊',
    description: t('v3/chat:categories.analyze.description', '分析数据、效率和进展'),
    prompts: [
      {
        id: 'analyze-efficiency',
        text: t('v3/chat:categories.analyze.prompts.efficiency.text', '分析我这两天的效率，看看'),
        description: t('v3/chat:categories.analyze.prompts.efficiency.description', '效率分析报告')
      },
      {
        id: 'analyze-tasks',
        text: t('v3/chat:categories.analyze.prompts.tasks.text', '看看我接下来还要做什么'),
        description: t('v3/chat:categories.analyze.prompts.tasks.description', '任务进度分析')
      },
      {
        id: 'analyze-time',
        text: t('v3/chat:categories.analyze.prompts.time.text', '分析我的时间分配，重点关注'),
        description: t('v3/chat:categories.analyze.prompts.time.description', '时间管理分析')
      },
      {
        id: 'analyze-progress',
        text: t('v3/chat:categories.analyze.prompts.progress.text', '总结一下我最近的工作进展'),
        description: t('v3/chat:categories.analyze.prompts.progress.description', '进展总结报告')
      },
      {
        id: 'analyze-habits',
        text: t('v3/chat:categories.analyze.prompts.habits.text', '帮我分析我的习惯模式'),
        description: t('v3/chat:categories.analyze.prompts.habits.description', '习惯分析')
      }
    ]
  },
  {
    id: 'plan',
    title: t('v3/chat:categories.plan.title', '计划'),
    icon: '📅',
    description: t('v3/chat:categories.plan.description', '制定计划和安排时间'),
    prompts: [
      {
        id: 'plan-week',
        text: t('v3/chat:categories.plan.prompts.week.text', '帮我规划下周的安排，重点是'),
        description: t('v3/chat:categories.plan.prompts.week.description', '周计划制定')
      },
      {
        id: 'plan-day',
        text: t('v3/chat:categories.plan.prompts.day.text', '优化我明天的日程，考虑到'),
        description: t('v3/chat:categories.plan.prompts.day.description', '日程优化')
      },
      {
        id: 'plan-goal',
        text: t('v3/chat:categories.plan.prompts.goal.text', '制定一个目标计划，我想要'),
        description: t('v3/chat:categories.plan.prompts.goal.description', '目标规划')
      },
      {
        id: 'plan-learning',
        text: t('v3/chat:categories.plan.prompts.learning.text', '安排学习计划，我想学习'),
        description: t('v3/chat:categories.plan.prompts.learning.description', '学习计划')
      },
      {
        id: 'plan-vacation',
        text: t('v3/chat:categories.plan.prompts.vacation.text', '帮我规划假期安排'),
        description: t('v3/chat:categories.plan.prompts.vacation.description', '假期规划')
      }
    ]
  },
  {
    id: 'reorganize',
    title: t('v3/chat:categories.reorganize.title', '重排'),
    icon: '🔄',
    description: t('v3/chat:categories.reorganize.description', '调整和重新安排'),
    prompts: [
      {
        id: 'reschedule-tasks',
        text: t('v3/chat:categories.reorganize.prompts.tasks.text', '重新安排我的任务优先级，因为'),
        description: t('v3/chat:categories.reorganize.prompts.tasks.description', '任务重排')
      },
      {
        id: 'reschedule-meeting',
        text: t('v3/chat:categories.reorganize.prompts.meeting.text', '调整会议时间，改到'),
        description: t('v3/chat:categories.reorganize.prompts.meeting.description', '会议重排')
      },
      {
        id: 'reorganize-schedule',
        text: t('v3/chat:categories.reorganize.prompts.schedule.text', '重新组织我的日程安排'),
        description: t('v3/chat:categories.reorganize.prompts.schedule.description', '日程重组')
      },
      {
        id: 'postpone-tasks',
        text: t('v3/chat:categories.reorganize.prompts.postpone.text', '推迟一些任务到明天，优先处理'),
        description: t('v3/chat:categories.reorganize.prompts.postpone.description', '任务延期')
      },
      {
        id: 'balance-workload',
        text: t('v3/chat:categories.reorganize.prompts.balance.text', '平衡我的工作负荷'),
        description: t('v3/chat:categories.reorganize.prompts.balance.description', '负荷平衡')
      }
    ]
  },
  {
    id: 'chat',
    title: t('v3/chat:categories.chat.title', '聊人生'),
    icon: '💭',
    description: t('v3/chat:categories.chat.description', '轻松聊天和思考人生'),
    prompts: [
      {
        id: 'chat-mood',
        text: t('v3/chat:categories.chat.prompts.mood.text', '我今天心情有点'),
        description: t('v3/chat:categories.chat.prompts.mood.description', '心情分享')
      },
      {
        id: 'chat-thoughts',
        text: t('v3/chat:categories.chat.prompts.thoughts.text', '我在思考一个问题'),
        description: t('v3/chat:categories.chat.prompts.thoughts.description', '思考交流')
      },
      {
        id: 'chat-advice',
        text: t('v3/chat:categories.chat.prompts.advice.text', '给我一些建议，关于'),
        description: t('v3/chat:categories.chat.prompts.advice.description', '寻求建议')
      },
      {
        id: 'chat-reflection',
        text: t('v3/chat:categories.chat.prompts.reflection.text', '帮我反思一下最近的'),
        description: t('v3/chat:categories.chat.prompts.reflection.description', '人生反思')
      },
      {
        id: 'chat-inspiration',
        text: t('v3/chat:categories.chat.prompts.inspiration.text', '我需要一些灵感和动力'),
        description: t('v3/chat:categories.chat.prompts.inspiration.description', '寻找灵感')
      }
    ]
  },
  {
    id: 'help',
    title: t('v3/chat:categories.help.title', '帮助'),
    icon: '🤝',
    description: t('v3/chat:categories.help.description', '获取帮助和解决问题'),
    prompts: [
      {
        id: 'help-problem',
        text: t('v3/chat:categories.help.prompts.problem.text', '我遇到了一个问题'),
        description: t('v3/chat:categories.help.prompts.problem.description', '问题求助')
      },
      {
        id: 'help-decision',
        text: t('v3/chat:categories.help.prompts.decision.text', '帮我做一个决定，关于'),
        description: t('v3/chat:categories.help.prompts.decision.description', '决策帮助')
      },
      {
        id: 'help-learn',
        text: t('v3/chat:categories.help.prompts.learn.text', '教我如何'),
        description: t('v3/chat:categories.help.prompts.learn.description', '学习指导')
      },
      {
        id: 'help-explain',
        text: t('v3/chat:categories.help.prompts.explain.text', '解释一下'),
        description: t('v3/chat:categories.help.prompts.explain.description', '概念解释')
      },
      {
        id: 'help-troubleshoot',
        text: t('v3/chat:categories.help.prompts.troubleshoot.text', '帮我排查问题'),
        description: t('v3/chat:categories.help.prompts.troubleshoot.description', '故障排除')
      }
    ]
  }
];

// 获取随机欢迎消息（集成时间问候语）
export const getRandomWelcomeMessage = (t: any): string => {
  // 30% 概率尝试获取时间相关问候语
  const timeGreeting = getTimeBasedGreeting(0.3);

  // 如果有时间问候语且符合当前时间，使用时间问候语
  if (timeGreeting) {
    return timeGreeting;
  }

  // 否则使用通用欢迎语
  const welcomeMessages = getWelcomeMessages(t);
  return welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
};

// 获取随机副标题
export const getRandomSubMessage = (t: any): string => {
  const subMessages = getSubMessages(t);
  return subMessages[Math.floor(Math.random() * subMessages.length)];
};
